import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { DASHBOARD_ROUTES } from '@worktracker/shared';
import { AuthProvider } from '@/context/auth-context';
import { ProtectedRoute } from '@/components/protected-route';
import { Layout } from '@/components/layout';
import { SidebarProvider } from '@/components/sidebar';
import { RouteErrorBoundary } from '@/components/route-error-boundary';
import { Spinner } from '@/components/ui/spinner';
import { LoginPage } from '@/pages/login';

// Lazy-loaded page components (code-split into separate chunks)
const ReportsPage = lazy(() => import('@/pages/reports').then((m) => ({ default: m.ReportsPage })));
const EmployeesPage = lazy(() =>
  import('@/pages/employees').then((m) => ({ default: m.EmployeesPage })),
);
const ManagerHomePage = lazy(() =>
  import('@/pages/manager-home').then((m) => ({ default: m.ManagerHomePage })),
);
const EmployeeDetailPage = lazy(() =>
  import('@/pages/employee-detail').then((m) => ({ default: m.EmployeeDetailPage })),
);
const DailyViewPage = lazy(() =>
  import('@/pages/daily-view').then((m) => ({ default: m.DailyViewPage })),
);
const FlagsPage = lazy(() => import('@/pages/flags').then((m) => ({ default: m.FlagsPage })));
const SettingsPage = lazy(() =>
  import('@/pages/settings').then((m) => ({ default: m.SettingsPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
    },
  },
});

const routeFallback = (
  <div className="flex h-full items-center justify-center">
    <Spinner size="lg" />
  </div>
);

const lazyRoute = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <RouteErrorBoundary>
    <Suspense fallback={routeFallback}>
      <Component />
    </Suspense>
  </RouteErrorBoundary>
);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <SidebarProvider>
            <Routes>
              <Route path={DASHBOARD_ROUTES.LOGIN} element={<LoginPage />} />
              <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  <Route
                    path={DASHBOARD_ROUTES.HOME}
                    element={<Navigate to={DASHBOARD_ROUTES.DASHBOARD} replace />}
                  />
                  <Route path={DASHBOARD_ROUTES.DASHBOARD} element={lazyRoute(ManagerHomePage)} />
                  <Route path={DASHBOARD_ROUTES.EMPLOYEES} element={lazyRoute(EmployeesPage)} />
                  <Route
                    path={DASHBOARD_ROUTES.EMPLOYEE_DETAIL}
                    element={lazyRoute(EmployeeDetailPage)}
                  />
                  <Route path={DASHBOARD_ROUTES.DAILY_VIEW} element={lazyRoute(DailyViewPage)} />
                  <Route path={DASHBOARD_ROUTES.FLAGS} element={lazyRoute(FlagsPage)} />
                  <Route path={DASHBOARD_ROUTES.SETTINGS} element={lazyRoute(SettingsPage)} />
                  <Route path={DASHBOARD_ROUTES.REPORTS} element={lazyRoute(ReportsPage)} />
                </Route>
              </Route>
            </Routes>
          </SidebarProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
